import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import WorkflowBuilder from './components/WorkflowBuilder';
import WorkflowList from './components/WorkflowList';
import WorkflowAnalytics from './components/WorkflowAnalytics';
import TriggerConfiguration from './components/TriggerConfiguration';
import workflowService from '../../services/workflowService';
import PageLayout from '../../components/ui/PageLayout';

const WorkflowManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('workflows');
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWorkflowBuilder, setShowWorkflowBuilder] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadWorkflows();
    } else {
      setLoading(false);
      setError('User not authenticated');
    }
  }, [user?.id]);

  const loadWorkflows = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await workflowService?.getWorkflows?.(user?.id);
      
      if (result?.error) {
        console.error('Error loading workflows:', result?.error);
        setError(result?.error);
      } else {
        setWorkflows(result?.data || []);
      }
    } catch (err) {
      console.error('Failed to load workflows:', err);
      setError('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkflow = () => {
    setSelectedWorkflow(null);
    setShowWorkflowBuilder(true);
  };

  const handleEditWorkflow = (workflow) => {
    setSelectedWorkflow(workflow);
    setShowWorkflowBuilder(true);
  };

  const handleWorkflowSaved = () => {
    setShowWorkflowBuilder(false);
    setSelectedWorkflow(null);
    loadWorkflows();
  };

  const handleWorkflowDeleted = () => {
    loadWorkflows();
  };

  return (
    <PageLayout>
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Workflow Management</h1>
          <p className="text-muted-foreground">
            Automate your sales processes with intelligent workflows and triggers
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            onClick={() => {/* Export workflows */}}
          >
            Export Workflows
          </Button>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => {
              setSelectedWorkflow(null);
              setShowWorkflowBuilder(true);
            }}
          >
            Create Workflow
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-destructive" />
            <span className="text-destructive text-sm">{error}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setError(null)}
              className="ml-auto"
            >
              <Icon name="X" size={14} />
            </Button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading workflows...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('workflows')}
                  className={`
                    flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm
                    ${activeTab === 'workflows' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }
                  `}
                >
                  <Icon name="Zap" size={16} />
                  <span>Workflows</span>
                </button>
                <button
                  onClick={() => setActiveTab('builder')}
                  className={`
                    flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm
                    ${activeTab === 'builder' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }
                  `}
                >
                  <Icon name="GitBranch" size={16} />
                  <span>Builder</span>
                </button>
                <button
                  onClick={() => setActiveTab('analytics')}
                  className={`
                    flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm
                    ${activeTab === 'analytics' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }
                  `}
                >
                  <Icon name="BarChart3" size={16} />
                  <span>Analytics</span>
                </button>
                <button
                  onClick={() => setActiveTab('triggers')}
                  className={`
                    flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm
                    ${activeTab === 'triggers' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }
                  `}
                >
                  <Icon name="Clock" size={16} />
                  <span>Triggers</span>
                </button>
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'workflows' && (
              <WorkflowList
                workflows={workflows}
                onEdit={(workflow) => {
                  setSelectedWorkflow(workflow);
                  setShowWorkflowBuilder(true);
                }}
                onDelete={() => loadWorkflows()}
                onCreateNew={() => {
                  setSelectedWorkflow(null);
                  setShowWorkflowBuilder(true);
                }}
              />
            )}

            {activeTab === 'builder' && (
              <WorkflowBuilder
                workflow={selectedWorkflow}
                onSave={() => {
                  setShowWorkflowBuilder(false);
                  setSelectedWorkflow(null);
                  loadWorkflows();
                }}
                onCancel={() => setShowWorkflowBuilder(false)}
              />
            )}

            {activeTab === 'analytics' && (
              <WorkflowAnalytics
                workflows={workflows}
                userId={user?.id}
              />
            )}

            {activeTab === 'triggers' && (
              <TriggerConfiguration
                workflows={workflows}
                onWorkflowUpdate={loadWorkflows}
              />
            )}
          </div>

          {/* Quick Stats */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Active Workflows</p>
                  <p className="text-2xl font-bold text-foreground">
                    {workflows?.filter(w => w?.status === 'active' || w?.status === 'running')?.length || 0}
                  </p>
                </div>
                <Icon name="Zap" size={24} className="text-primary" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Executions</p>
                  <p className="text-2xl font-bold text-foreground">
                    {workflows?.reduce((sum, w) => sum + (w?.execution_count || w?.executionCount || 0), 0) || 0}
                  </p>
                </div>
                <Icon name="Play" size={24} className="text-success" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
                  <p className="text-2xl font-bold text-foreground">
                    {workflows?.length > 0 
                      ? Math.round(
                          (workflows?.reduce((sum, w) => sum + (w?.success_count || w?.successCount || 0), 0) /
                           Math.max(workflows?.reduce((sum, w) => sum + (w?.execution_count || w?.executionCount || 0), 0), 1)) * 100
                        )
                      : 0
                    }%
                  </p>
                </div>
                <Icon name="TrendingUp" size={24} className="text-warning" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Draft Workflows</p>
                  <p className="text-2xl font-bold text-foreground">
                    {workflows?.filter(w => w?.status === 'draft' || w?.status === 'inactive')?.length || 0}
                  </p>
                </div>
                <Icon name="FileText" size={24} className="text-muted-foreground" />
              </div>
            </div>
          </div>
        </>
      )}
    </PageLayout>
  );
};

export default WorkflowManagement;